import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { GlassCard, CustomInput, GradientButton, LoadingOverlay, useAlert } from '../../components';
import { getActivePaymentChannels, createRechargeRequest } from '../../services/firestoreService';
import { formatCurrency, isValidUTR, copyToClipboard } from '../../utils/helpers';
import { pickImage } from '../../utils/imageUtils';
import './RechargeScreen.css';

const MIN_RECHARGE = 680;
const QUICK_AMOUNTS = [680, 1620, 3500, 7800];

export const RechargeScreen = () => {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const alert = useAlert();

    const [channels, setChannels] = useState([]);
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [amount, setAmount] = useState('');
    const [utrNumber, setUtrNumber] = useState('');
    const [proofBase64, setProofBase64] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [step, setStep] = useState(1); // 1: Amount/Channel, 2: Payment
    const [hasPaid, setHasPaid] = useState(false);
    const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds

    useEffect(() => {
        loadChannels();
    }, []);

    useEffect(() => {
        let timer;
        if (step === 2 && timeLeft > 0) {
            timer = setInterval(() => {
                setTimeLeft(prev => prev - 1);
            }, 1000);
        } else if (timeLeft === 0) {
            alert.showInfo('Time Expired', 'Payment time has expired. Please try again.');
            setStep(1);
            setTimeLeft(300);
        }
        return () => clearInterval(timer);
    }, [step, timeLeft]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const loadChannels = async () => {
        try {
            const data = await getActivePaymentChannels();
            setChannels(data);
            if (data.length > 0) {
                setSelectedChannel(data[0]);
            }
        } catch (error) {
            alert.showError('Error', 'Failed to load payment channels');
        } finally {
            setLoading(false);
        }
    };

    const handleImagePick = async () => {
        try {
            const result = await pickImage();
            if (result) {
                setProofBase64(result.base64);
                setErrors({ ...errors, proof: null });
            }
        } catch (error) {
            alert.showError('Error', 'Failed to pick image');
        }
    };

    const handleNextStep = () => {
        const newErrors = {};
        const amountNum = parseInt(amount);

        if (!amount || isNaN(amountNum)) {
            newErrors.amount = 'Please enter a valid amount';
        } else if (amountNum < MIN_RECHARGE) {
            newErrors.amount = `Minimum recharge is ${formatCurrency(MIN_RECHARGE)}`;
        }

        if (!selectedChannel) {
            newErrors.channel = 'Please select a payment channel';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setErrors({});
        setStep(2);
        setTimeLeft(300);
    };

    const validateFinalForm = () => {
        const newErrors = {};

        if (!utrNumber) {
            newErrors.utr = 'UTR number is required';
        } else if (!isValidUTR(utrNumber)) {
            newErrors.utr = 'UTR must be 12 digits';
        }

        if (!proofBase64) {
            newErrors.proof = 'Please upload payment screenshot';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateFinalForm()) return;

        setSubmitting(true);
        try {
            await createRechargeRequest(parseInt(amount), utrNumber, proofBase64);
            alert.showSuccess(
                'Success',
                'Recharge request submitted successfully. Your balance will be updated once approved.',
                () => navigate(-1)
            );
        } catch (error) {
            alert.showError('Error', error.message || 'Failed to submit request');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopyUPI = async () => {
        if (selectedChannel?.upiId) {
            const success = await copyToClipboard(selectedChannel.upiId);
            if (success) {
                alert.showSuccess('Copied', 'UPI ID copied to clipboard');
            }
        }
    };

    if (loading) {
        return <LoadingOverlay visible message="Loading payment channels..." />;
    }

    if (channels.length === 0) {
        return (
            <div className="recharge-screen">
                <div className="screen-header">
                    <button className="back-btn" onClick={() => navigate(-1)}>
                        <span className="material-icons">arrow_back</span>
                    </button>
                    <h1 className="screen-title">Recharge</h1>
                    <div style={{ width: 40 }}></div>
                </div>
                <div className="empty-state">
                    <span className="material-icons empty-icon">credit_card_off</span>
                    <h3>No Payment Channels</h3>
                    <p>Payment channels are not available at the moment. Please try again later.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="recharge-screen">
            <LoadingOverlay visible={submitting} message="Submitting request..." />

            <div className="screen-header">
                <button className="back-btn" onClick={() => step === 2 ? setStep(1) : navigate(-1)}>
                    <span className="material-icons">arrow_back</span>
                </button>
                <h1 className="screen-title">{step === 1 ? 'Recharge' : 'Payment'}</h1>
                <div style={{ width: 40 }}></div>
            </div>

            <div className="recharge-content">
                {step === 1 ? (
                    <>
                        {/* Step 1: Amount & Rules */}
                        <div className="step-section">
                            <h2 className="section-title">Enter Amount</h2>
                            <div className="quick-amounts">
                                {QUICK_AMOUNTS.map((amt) => (
                                    <button
                                        key={amt}
                                        className={`quick-amount-btn ${amount === String(amt) ? 'selected' : ''}`}
                                        onClick={() => setAmount(String(amt))}
                                    >
                                        {formatCurrency(amt)}
                                    </button>
                                ))}
                            </div>
                            <CustomInput
                                placeholder="Enter custom amount"
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                leftIcon="currency_rupee"
                                error={errors.amount}
                            />
                            <p className="helper-text">Min: {formatCurrency(MIN_RECHARGE)} | Max: {formatCurrency(500000)}</p>
                        </div>

                        <div className="step-section">
                            <h2 className="section-title">Selection Channel</h2>
                            <div className="channels-grid">
                                {channels.map((channel) => (
                                    <div
                                        key={channel.id}
                                        className={`channel-card-new ${selectedChannel?.id === channel.id ? 'selected' : ''}`}
                                        onClick={() => setSelectedChannel(channel)}
                                    >
                                        <div className="channel-info">
                                            <span className="material-icons channel-icon">bolt</span>
                                            <span className="channel-name">{channel.name}</span>
                                        </div>
                                        <div className="radio-circle">
                                            {selectedChannel?.id === channel.id && <div className="radio-inner"></div>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {errors.channel && <span className="error-text">{errors.channel}</span>}
                        </div>

                        <GlassCard className="recharge-rules">
                            <h3 className="rules-title">Recharge Rules</h3>
                            <ul className="rules-list">
                                <li>Recharge time: 24/7 (Mon-Sun).</li>
                                <li>Arrival time: Within 5-30 minutes.</li>
                                <li>Minimum recharge amount: {formatCurrency(MIN_RECHARGE)}.</li>
                                <li>Ensure the UTR number is correct to avoid delays.</li>
                                <li>Keep the screenshot of payment for support if needed.</li>
                            </ul>
                        </GlassCard>

                        <div className="bottom-action">
                            <GradientButton
                                title="Pay Now"
                                onClick={handleNextStep}
                                icon={<span className="material-icons">payments</span>}
                            />
                        </div>
                    </>
                ) : (
                    <>
                        {/* Step 2: Payment Details */}
                        <GlassCard className="payment-gateway-card">
                            <div className="timer-section">
                                <span className="timer-label">Payment session expires in</span>
                                <span className="timer-value">{formatTime(timeLeft)}</span>
                            </div>

                            <div className="payable-amount">
                                <span className="amount-label">Payable Amount</span>
                                <span className="amount-value">{formatCurrency(amount)}</span>
                            </div>

                            <div className="qr-container-new">
                                {selectedChannel?.qrCodeBase64 ? (
                                    <img src={selectedChannel.qrCodeBase64} alt="Payment QR" className="payment-qr" />
                                ) : (
                                    <div className="qr-placeholder">
                                        <span className="material-icons">qr_code_2</span>
                                    </div>
                                )}
                            </div>

                            <div className="upi-id-box">
                                <div className="upi-details">
                                    <span className="upi-label">UPI ID</span>
                                    <span className="upi-value">{selectedChannel?.upiId || 'N/A'}</span>
                                </div>
                                <button className="copy-action-btn" onClick={handleCopyUPI}>
                                    <span className="material-icons">content_copy</span>
                                </button>
                            </div>

                            <div className="payment-checkpoint">
                                <button
                                    className={`checkpoint-btn ${hasPaid ? 'active' : ''}`}
                                    onClick={() => setHasPaid(!hasPaid)}
                                >
                                    <span className="material-icons">
                                        {hasPaid ? 'check_box' : 'check_box_outline_blank'}
                                    </span>
                                    I have made payment
                                </button>
                            </div>

                            {hasPaid && (
                                <div className="submission-form animate-in">
                                    <div className="input-group">
                                        <label className="input-label">Enter 12-Digit UTR Number</label>
                                        <CustomInput
                                            placeholder="Enter UTR here"
                                            value={utrNumber}
                                            onChange={(e) => setUtrNumber(e.target.value.replace(/\D/g, '').slice(0, 12))}
                                            error={errors.utr}
                                        />
                                    </div>

                                    <div className="upload-group">
                                        <label className="input-label">Upload Payment Screenshot</label>
                                        {proofBase64 ? (
                                            <div className="proof-preview">
                                                <img src={proofBase64} alt="Proof" />
                                                <button className="remove-btn" onClick={() => setProofBase64(null)}>
                                                    <span className="material-icons">close</span>
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="upload-placeholder" onClick={handleImagePick}>
                                                <span className="material-icons">add_a_photo</span>
                                                <span>Add Screenshot</span>
                                            </div>
                                        )}
                                        {errors.proof && <span className="error-text">{errors.proof}</span>}
                                    </div>

                                    <GradientButton
                                        title="Submit Recharge Request"
                                        onClick={handleSubmit}
                                        loading={submitting}
                                        icon={<span className="material-icons">check_circle</span>}
                                    />
                                </div>
                            )}
                        </GlassCard>

                        <div className="gateway-footer">
                            <span className="material-icons">security</span>
                            <span>Secure Payment Powered by Yonex AI</span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default RechargeScreen;
